import { useEffect, useState } from "react";

import { api } from "../../api.js";
import type { ChartTableSelection } from "../../lib/chartTables.js";
import type { FilterState, WifiOverview, WifiTableSummary } from "../../types.js";
import { ChartCard } from "../ChartCard.js";

const CRITERIA = `Source: device_telemetry_minutes.wifi, one row per device per minute (the first report in that minute). device_network_tests supplies the speed-test range. The Game filter does not apply.

Left good/bad bar (signal / base link) — a sampled minute is bad if any of these is true:
• Signal: not connected, or rssi_dbm below −80
• TX link: connected and tx_link_speed_mbps (else link_speed_mbps) under 5 Mbps
• RX link is ignored for this bar

Worst issue is Signal or TX link, whichever has more bad minutes. Rows are name order (table · venue). Left half is the good/bad bar; right half is the speed-test range.

Speed tests (base internet): worst and best succeeded download_mbps / upload_mbps in the window. Each row is a thin grey scale line + threshold tick; the thick bar is the worst–best range — red below the threshold (↓25 / ↑5), green once it passes. Triangles match. ⚠️ when any test is under threshold.

Click the red or green segment: opens the closable Health panel below with Signal (RSSI) & Network Speed (TX) over time (fixed scales −100…0 dBm / 0…100 Mbps; dashed bad lines at −80 dBm / 5 Mbps), the minute list, and speed tests in a side panel. Not Raw Events.`;

const SPEED_MAX = 100;
const SPEED_DOWNLOAD_BAD_BELOW = 25;
const SPEED_UPLOAD_BAD_BELOW = 5;

interface WifiByTableCardProps {
  filters: FilterState;
  chartTables: ChartTableSelection;
  onOpenWifiMinutes: (deviceId: string, deviceLabel: string, quality: "bad" | "good") => void;
}

function pct(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 100);
}

function mbps(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function rangeLabel(worst: number | null, best: number | null): string | null {
  if (worst == null || best == null) return null;
  if (worst === best) return `${mbps(worst)} Mbps`;
  return `worst ${mbps(worst)} – best ${mbps(best)} Mbps`;
}

function labelFor(row: WifiTableSummary): string {
  return row.venueName ? `${row.deviceName} · ${row.venueName}` : row.deviceName;
}

function rowVisible(deviceId: string, chartTables: ChartTableSelection, filters: FilterState): boolean {
  if (filters.deviceId !== "all") return true;
  if (chartTables === "all") return true;
  if (chartTables === "none") return false;
  return chartTables.includes(deviceId);
}

function GoodBadBar({
  badPct,
  onBad,
  onGood,
}: {
  badPct: number;
  onBad: (event: MouseEvent) => void;
  onGood: (event: MouseEvent) => void;
}) {
  const goodPct = 100 - badPct;
  return (
    <div className="flex h-7 min-w-0 flex-1 overflow-hidden rounded-sm border border-border">
      {badPct > 0 ? (
        <button
          type="button"
          className="bg-err text-[11px] font-semibold text-[#0b1220]"
          style={{ width: `${badPct}%` }}
          title="Click: Health panel — Signal & Network Speed chart + bad Wi-Fi minutes for this table."
          onClick={(event) => onBad(event.nativeEvent)}
          onAuxClick={(event) => {
            if (event.button !== 1) return;
            event.preventDefault();
            onBad(event.nativeEvent);
          }}
        >
          {badPct >= 18 ? `Bad ${badPct}%` : ""}
        </button>
      ) : null}
      {goodPct > 0 ? (
        <button
          type="button"
          className="bg-ok text-[11px] font-semibold text-[#0b1220]"
          style={{ width: `${goodPct}%` }}
          title="Click: Health panel — Signal & Network Speed chart + good Wi-Fi minutes for this table."
          onClick={(event) => onGood(event.nativeEvent)}
          onAuxClick={(event) => {
            if (event.button !== 1) return;
            event.preventDefault();
            onGood(event.nativeEvent);
          }}
        >
          {goodPct >= 18 ? `Good ${goodPct}%` : ""}
        </button>
      ) : null}
    </div>
  );
}

function SpeedTrack({
  worst,
  best,
  slowBelowMbps,
}: {
  worst: number;
  best: number;
  slowBelowMbps: number;
}) {
  const leftPct = (Math.max(0, Math.min(SPEED_MAX, worst)) / SPEED_MAX) * 100;
  const rightPct = (Math.max(0, Math.min(SPEED_MAX, best)) / SPEED_MAX) * 100;
  const spanPct = Math.max(rightPct - leftPct, 0);
  const slowPct = Math.max(0, Math.min(100, (slowBelowMbps / SPEED_MAX) * 100));
  const leftOk = worst >= slowBelowMbps;
  const rightOk = best >= slowBelowMbps;
  const redEnd = Math.min(rightPct, slowPct);
  const greenStart = Math.max(leftPct, slowPct);
  const redWidth = Math.max(0, redEnd - leftPct);
  const greenWidth = Math.max(0, rightPct - greenStart);
  const pointOnly = spanPct === 0;

  return (
    <div className="relative h-5 min-w-0 flex-1">
      <div className="absolute top-1/2 right-0 left-0 h-px -translate-y-1/2 bg-border" />
      <div
        className="absolute top-1/2 h-2.5 w-px -translate-y-1/2 bg-text-secondary/60"
        style={{ left: `${slowPct}%` }}
        title={`Threshold ${slowBelowMbps} Mbps`}
      />
      {pointOnly ? (
        <div
          className={`absolute top-1/2 h-2.5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-sm ${
            leftOk ? "bg-ok" : "bg-err"
          }`}
          style={{ left: `${leftPct}%` }}
        />
      ) : (
        <>
          {redWidth > 0 ? (
            <div
              className="absolute top-1/2 h-2.5 -translate-y-1/2 rounded-l-sm bg-err"
              style={{
                left: `${leftPct}%`,
                width: `${redWidth}%`,
                borderTopRightRadius: greenWidth > 0 ? 0 : undefined,
                borderBottomRightRadius: greenWidth > 0 ? 0 : undefined,
              }}
            />
          ) : null}
          {greenWidth > 0 ? (
            <div
              className="absolute top-1/2 h-2.5 -translate-y-1/2 rounded-r-sm bg-ok"
              style={{
                left: `${greenStart}%`,
                width: `${greenWidth}%`,
                borderTopLeftRadius: redWidth > 0 ? 0 : undefined,
                borderBottomLeftRadius: redWidth > 0 ? 0 : undefined,
              }}
            />
          ) : null}
        </>
      )}
      <div
        className={`absolute top-0 h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[7px] border-x-transparent ${
          leftOk ? "border-t-ok" : "border-t-err"
        }`}
        style={{ left: `${leftPct}%` }}
      />
      {spanPct > 0 ? (
        <div
          className={`absolute top-0 h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[7px] border-x-transparent ${
            rightOk ? "border-t-ok" : "border-t-err"
          }`}
          style={{ left: `${leftPct + spanPct}%` }}
        />
      ) : null}
    </div>
  );
}

function SpeedRow({
  label,
  worst,
  best,
  slowBelowMbps,
}: {
  label: string;
  worst: number | null;
  best: number | null;
  slowBelowMbps: number;
}) {
  const text = rangeLabel(worst, best);
  if (text == null || worst == null || best == null) return null;
  const warn = worst < slowBelowMbps;
  const warnReason = `Below ${slowBelowMbps} Mbps on at least one succeeded speed test in this window`;
  return (
    <div className="flex items-center gap-2">
      <div className="w-[148px] shrink-0 text-[11px] text-text-secondary">{label}</div>
      <SpeedTrack worst={worst} best={best} slowBelowMbps={slowBelowMbps} />
      <div className="w-[168px] shrink-0 text-right text-[11px] text-text-secondary">
        {text}
        {warn ? (
          <span className="ml-1" title={warnReason} aria-label={warnReason}>
            ⚠️
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function WifiByTableCard({
  filters,
  chartTables,
  onOpenWifiMinutes,
}: WifiByTableCardProps) {
  const [overview, setOverview] = useState<WifiOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .healthWifi(filters)
      .then((next) => {
        if (!cancelled) setOverview(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load Wi-Fi");
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const rows = (overview?.tables ?? []).filter((row) =>
    rowVisible(row.deviceId, chartTables, filters),
  );
  const downloadBadBelow =
    overview?.speedTestDownloadBadBelowMbps ?? SPEED_DOWNLOAD_BAD_BELOW;
  const uploadBadBelow = overview?.speedTestUploadBadBelowMbps ?? SPEED_UPLOAD_BAD_BELOW;

  return (
    <ChartCard title="Wi-Fi by table" criteria={CRITERIA}>
      {error ? <p className="text-xs text-err">{error}</p> : null}
      {!error && overview && !overview.available ? (
        <p className="text-xs text-text-secondary">
          Wi-Fi history is not in this database yet (`device_telemetry_minutes`).
        </p>
      ) : null}
      {!error && !overview ? <p className="text-xs text-text-secondary">Loading Wi-Fi…</p> : null}
      {!error && overview?.available && rows.length === 0 ? (
        <p className="text-xs text-text-secondary">No sampled Wi-Fi minutes in this window.</p>
      ) : null}
      <div className="flex flex-col">
        {rows.map((row, index) => {
          const badPct = pct(row.badMinutes, row.minutes);
          const goodPct = 100 - badPct;
          const side =
            badPct > 0 && badPct < 18
              ? `Bad ${badPct}%`
              : goodPct > 0 && goodPct < 18
                ? `Good ${goodPct}%`
                : null;
          return (
            <div
              key={row.deviceId}
              className={`grid grid-cols-1 items-center gap-x-6 gap-y-1 py-2.5 xl:grid-cols-2 ${
                index > 0 ? "border-t border-border-subtle" : ""
              }`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="w-[168px] shrink-0 truncate text-sm font-semibold text-text-primary">
                  {labelFor(row)}
                </div>
                <GoodBadBar
                  badPct={badPct}
                  onBad={() => onOpenWifiMinutes(row.deviceId, labelFor(row), "bad")}
                  onGood={() => onOpenWifiMinutes(row.deviceId, labelFor(row), "good")}
                />
                <div className="w-[132px] shrink-0 text-xs text-text-primary">
                  {side ? <div className="text-text-secondary">{side}</div> : null}
                  {row.worstIssue && row.worstIssue !== "RX link" ? (
                    <div>
                      Worst issue: <span className="font-semibold">{row.worstIssue}</span>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="flex min-w-0 flex-col gap-1">
                <SpeedRow
                  label="Speed tests download"
                  worst={row.downloadWorstMbps}
                  best={row.downloadBestMbps}
                  slowBelowMbps={downloadBadBelow}
                />
                <SpeedRow
                  label="Speed tests upload"
                  worst={row.uploadWorstMbps}
                  best={row.uploadBestMbps}
                  slowBelowMbps={uploadBadBelow}
                />
              </div>
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
}
